import { FC, useContext, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, GridLegacy as Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import DashPage from '../dash/DashPage';
import { SessionContext } from '../../utility/useSession';
import { IChartProps } from '../../components/charts/common/types';
import { IInsightsPageTab, IInsightsPage, IPreferences } from '../../logic/api';
import { TabbedMenu } from './components/tabbed-menu';
import { InsightsSidebar } from './components/sidebar';
import { Filters } from './components/filters';
import { BarChart } from '../../components/charts/bar-chart';
import { LineChart } from '../../components/charts/line-chart';
import { PieChart } from '../../components/charts/pie-chart';
import { TextChart } from '../../components/charts/text-chart';
import { HorizontalBarChart } from '../../components/charts/horizontal-bar-chart';
import { DonutChart } from '../../components/charts/donut-chart';
import { Table } from '../../components/charts/table/table';
import { UserParamsProvider } from '../../context/user-params-context';
import { GoogleMapsProvider } from '../../context/google-maps/google-maps';
import { getInsightsPages } from './get-pages';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { theme } from '../../theme';
import AccessDeniedMessage from '@/components/errors/AccessDeniedMessage';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { type Permission } from '@/components/users/rolesAndPermissions/types';

// Hacky way to allow for customisable line breaks via config
const LineBreakComponent: FC<IChartProps> = () => <div style={{ width: '100%' }} />;

const CHART_TYPE_MAP = new Map([
  [-1, LineBreakComponent],
  [0, BarChart],
  [1, LineChart],
  [2, PieChart],
  [3, TextChart],
  [4, HorizontalBarChart],
  [5, DonutChart],
  [6, Table],
])

const InsightsPage = () => {
  const session = useContext(SessionContext);
  const { t } = useTranslation()
  const { search } = useLocation()
  const { pageId } = useParams<{ pageId: string }>();
  const [pages, setPages] = useState<IInsightsPage[]>();
  const [currentPage, setCurrentPage] = useState<IInsightsPage>();
  const [currentTab, setCurrentTab] = useState<IInsightsPageTab>();
  const { hasPermission } = useUserPermissions();

  useEffect(() => {
    const loadChartConfig = async () => {
      try {
        const insightsPages = await getInsightsPages(session);
        setPages(insightsPages);

        const foundPage = insightsPages.find((page) => page.id === pageId)
        setCurrentPage(foundPage);
      } catch (error) {
        console.error('Error loading chartConfig:', error);
        setCurrentPage(undefined);
      }
    };

    loadChartConfig();
  }, [pageId]);

  useEffect(() => {
    if (!currentPage) {
      return;
    }

    const searchParams = new URLSearchParams(search)
    const updatedTab = currentPage.tabs.find((tab) => tab.id === searchParams.get('tabId')) || currentPage.tabs[0]
    setCurrentTab(updatedTab);
  }, [currentPage, search]);

  const handleTabChange = (tab: IInsightsPageTab) => {
    setCurrentTab(tab)
  }

  const pageTitle = currentPage
    ? `${currentPage.subTitle} / ${currentPage.pageTitle}`
    : t("errors.pageNotFound.message");

  // Check if we're in a loading state
  const isLoading = !pages;
  // Only render main content when we have data
  const shouldRenderContent = currentPage && currentTab;
  // A page can require its own category permission (e.g. Insights > Staff Activity).
  // The sidebar stays visible so the user can still reach categories they do have access to.
  const canViewPage = (page?: IInsightsPage): boolean =>
    !page?.permission || hasPermission(page.permission as Permission);
  const canViewCurrentPage = canViewPage(currentPage);

  if (!hasPermission("web.insights.read")) {
    return (
      <DashPage>
        <AccessDeniedMessage />
      </DashPage>
    );
  }

  return (
    <UserParamsProvider>
      <GoogleMapsProvider>
        <DashPage>
          {/* render sidebar if we have pages */}
          {pages && (
            <InsightsSidebar
              pages={pages}
              style={{
                position: 'fixed',
                top: 69,
                left: 51,
                bottom: 0,
                width: 250
              }}
            />
          )}

          {/* Main content */}
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <Typography variant="body1">{t("general.loading")}</Typography>
            </div>
          ) : (
            <main style={{
              position: 'absolute',
              left: 251, // insights sidebar width (+1 for border)
              right: 0
            }}>
              {!canViewCurrentPage ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                  <AccessDeniedMessage />
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h1"
                    textAlign="center"
                    fontSize={24}
                    color={theme.palette.brand.main}
                    fontWeight="bold"
                    margin={2}
                  >
                    {pageTitle}
                  </Typography>

                  {/* Render tabs if we have a valid page */}
                  {shouldRenderContent && (
                    <>
                      <TabbedMenu
                        currentPage={currentPage}
                        currentTab={currentTab}
                        onChange={handleTabChange}
                      />

                      <section style={{ padding: '16px 8px' }}>
                        {currentTab.charts?.length > 0 ? (
                          <>
                            {currentTab.criteria?.length > 0 && (
                              <Filters criteria={currentTab.criteria} />
                            )}
                            <Grid container spacing={2}>
                              {currentTab.charts.map((c, i) => {
                                if (!c || !c.uuid) {
                                  return null
                                }

                                if (c.requiredConditions) {
                                  if (c.requiredConditions.preferences) {
                                    const preferencesConditionsNotMet = Object.entries(c.requiredConditions.preferences).some(([key, expectedValue]) => {
                                      const prefs = session.current.site?.preferences
                                      if (!prefs) {
                                        return true
                                      }

                                      if (key in prefs) {
                                        return prefs[key as keyof IPreferences] !== expectedValue
                                      }

                                      return false
                                    })

                                    if (preferencesConditionsNotMet) {
                                      return null
                                    }
                                  }
                                }

                                const Component = CHART_TYPE_MAP.get(c.type)
                                if (!Component) {
                                  return null;
                                }

                                return (
                                  <Component
                                    key={`${c.uuid}-${currentPage?.id}-${currentTab.id}-${i}`}
                                    uuid={c.uuid}
                                  />
                                )
                              })}
                            </Grid>
                            {currentTab.id === "analysisTab" && (
                              <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{
                                  mt: 2,
                                  fontStyle: 'italic',
                                  color: 'text.secondary'
                                }}
                              >
                                {t("insights.dataLagNotice")}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" textAlign="center">
                            {t("insights.comingSoon")}
                          </Typography>
                        )}
                      </section>
                    </>
                  )}
                </>
              )}
            </main>
          )}
        </DashPage>
      </GoogleMapsProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
    </UserParamsProvider>
  );
};

export default InsightsPage;
